// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useState, useRef, useEffect, useMemo } from 'react';
import moment from 'moment-timezone';
import { useNavigate } from 'react-router-dom';
import { Card, CardActionArea, CardActions, CardMedia, Typography, IconButton, Tooltip, LinearProgress, Avatar, Stepper, Step, StepLabel, Popper, ClickAwayListener, Fade, Paper, MenuList, MenuItem } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import Brightness1Icon from '@mui/icons-material/Brightness1';
import { styled } from '@mui/material/styles';
import ProjectPreview from 'Assets/projectPreview.png';
import { GetProjectDetailsRoute, GetProjectFilesRoute } from 'Utils/routes';
import StepConnector, { stepConnectorClasses } from '@mui/material/StepConnector';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { Box, useTheme } from '@mui/system';
import { useSelector } from 'react-redux';
import { SetProjectIdAction } from 'Features/project/projectActions';
import { orderStatusCode } from 'Utils/constants';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import MoveProjectToFolderDialog from 'Components/MoveProjectToFolderDialog';
import ColorlibStepIcon from './StepIcon';
import { selectProjectInfo } from '../projectListSelector';
import { ReadThumbnailThunk } from '../projectListThunk';
import stylesDef from './styles';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import DeleteProjectDialog from 'Components/DeleteProjectDialog';

moment.tz.setDefault('America/New_York');

type ProjectListItemProps = {
  project: IProject;
  isInsideFolder?: boolean;
  handleMoveProjectToFolderSuccess?: (project?: IProject, folderId?: string) => void;
};

const ActiveLabels = ['Order placed', 'Processing', 'Checking for Quality', 'Done'];
const steps = ['Processing', 'AI', 'QC'];

const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 8,
    left: 'calc(-50% + 0px)',
    right: 'calc(50% + 0px)',
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor:
        '#08D38F',
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor:
        '#08D38F',
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 4,
    border: 0,
    backgroundColor: '#797979',
    borderRadius: 1,
    width: '100%',
  },
}));

const ProjectListItem = ({ project, isInsideFolder, handleMoveProjectToFolderSuccess }: ProjectListItemProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const projectInfo = useSelector((state) => selectProjectInfo(state, project, isInsideFolder));
  const { isInProgress, placed, autonomous, cleaning, complete, cannotDelete, orderFinalized, expressAIReady,
    orderEstimate, orderStatus, imageryRequest, rasterRequest, orderEstimatedDeliveryDate, orderDeliveredOn } = projectInfo;
  const activeStep = placed + autonomous + cleaning + complete;
  const { resources } = useAppSelector(
    (state) => state.auth,
  );
  const projectDetailsTab = 'projectDetailsTab' in resources;
  const opsTrainer = 'opsTrainer' in resources;

  // check project details resource as well as orderStatus not equal to 7 then navigate user to project details tab, else cad view tab
  let projectUrl = projectDetailsTab ? GetProjectDetailsRoute(project._id) : GetProjectFilesRoute(project._id);

  if (orderFinalized || expressAIReady) {
    projectUrl = GetProjectFilesRoute(project._id);
  }

  const thumbnail = !project.thumbnail || project.thumbnail.includes('undefined');

  useEffect(() => {
    if (!thumbnail) {
      dispatch(ReadThumbnailThunk(project._id));
    }
  }, []);

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const ref = useRef<any>(null);

  const [hovered, setHovered] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showMoveProjectToFolderDialog, setShowMoveProjectToFolderDialog] = useState(false);

  const enter = () => {
    dispatch(SetProjectIdAction(project._id));
    setHovered(true);
  };
  const leave = () => {
    dispatch(SetProjectIdAction(null));
    setHovered(false);
  };

  useEffect(() => {
    if (ref && ref.current) {
      ref.current.addEventListener('onmouseenter', enter);
      ref.current.addEventListener('onmouseleave', leave);
    }
    return () => {
      if (ref && ref.current) {
        ref.current.removeEventListener('onmouseenter', enter);
        ref.current.removeEventListener('onmouseleave', leave);
      }
    };
  }, [ref]);

  const isMenuOpen = useMemo(() => Boolean(anchorEl), [anchorEl]);

  const initials = project.creator.match(/\b\w/g) || [];
  
  const { requested, uploaded, rejected, notificationSent } = imageryRequest || {};
  
  const rasterRequested = rasterRequest?.requested;
  const imageryRequested = requested && !rejected && !uploaded && !notificationSent;

  const getDeliveryDateLabel = () => {
    let label = orderStatus === orderStatusCode.done ? 'Delivered on: ' : 'Delivery Date: ';

    if (orderEstimate || (orderStatus === orderStatusCode.canceled)) {
      label = '';
    }

    let date = '';

    if (orderEstimatedDeliveryDate) {
      date = moment(orderEstimatedDeliveryDate).format('LL');
    } else if (orderDeliveredOn) {
      date = moment(orderDeliveredOn).format('LL');
    } else {
      date = moment(project.updatedAt).format('LL');
    }

    return (
      <Typography variant="body2" sx={styles.deliveryEstimatedDate}>
        {(!orderEstimate && !(orderStatus === orderStatusCode.canceled)) && <CalendarMonthIcon fontSize="small" sx={styles.deliveryEstimatedDateIcon} />}
        <Box sx={styles.deliveryEstimatedDateText}>
          {label}
          {` ${date}`}
        </Box>
      </Typography>
    );
  };

  const menuItemRef = useRef<HTMLButtonElement | null>(null);
  const handleMenuClose = () => setAnchorEl(null);
  
  // useDraggable hook is used to make the project item draggable
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: project._id,
  });

  const draggableStyle = {
    transform: CSS.Translate.toString(transform),
  };

  const handleProjectClick = () => {
    navigate(projectUrl);
  };

  const dragThreshold = 5; // the maximum distance in pixels to consider a click instead of a drag
  const dragStart = useRef<{x: number, y: number} | null>(null);

  // Handle pointer down to start tracking the drag
  // this is used to determine if the pointer was moved more than the dragThreshold
  const handlePointerDown = (e: React.PointerEvent) => {
    dragStart.current = { x: e.clientX, y: e.clientY };
    if (listeners && listeners.onPointerDown) {
      listeners.onPointerDown(e);
    }
  };

  // Handle pointer up to determine if it was a click or a drag
  // if the pointer was moved more than the dragThreshold, it is considered a drag
  // Note: this was necessary due the large amount of events that were being triggered in the projects card (the dragging, the menu opening, the navigation, etc.)
  // this was the cleaner and most efficient way to handle it
  const handlePointerUp = (e: React.PointerEvent) => {
    const iconButton = document.getElementById(`menu-icon-${project._id}`);
    const moveToFolder = document.getElementById(`move-to-folder-${project._id}`);
    const deleteProject = document.getElementById(`delete-project-${project._id}`);

    if (moveToFolder && moveToFolder.contains(e.target as Node)) {
      handleMoveProjectToFolder();
      return;
    }

    if (deleteProject && deleteProject.contains(e.target as Node)) {
      handleDeleteProject();
      return;
    }

    if (iconButton && iconButton.contains(e.target as Node)) {
      setAnchorEl(menuItemRef.current);
      return;
    }

    if (!dragStart.current) return;
    const dx = Math.abs(e.clientX - dragStart.current.x);
    const dy = Math.abs(e.clientY - dragStart.current.y);
    if (dx < dragThreshold && dy < dragThreshold && !isMenuOpen) {
      handleProjectClick();
    }
    dragStart.current = null;
    if (listeners && listeners.onPointerUp) {
      listeners.onPointerUp(e);
    }
  };

  const handleMoveProjectToFolder = () => {
    window?.pendo?.track('Move To Folder');
    handleMenuClose();
    setShowMoveProjectToFolderDialog(true);
  }

  const handleDeleteProject = () => {
    window?.pendo?.track('Delete Project');
    handleMenuClose();
    setShowDeleteConfirmation(true);
  }

  return (
    <Box
      ref={ref}
      sx={[styles.cardWrapper, draggableStyle]}
      onMouseEnter={enter}
      onMouseLeave={leave}
    >
      {project.deleting && (
        <>
          <LinearProgress sx={styles.progress} />
          <Box sx={styles.loadingBackdrop} />
        </>
      )}
      <Card sx={styles.projectItem}>
        <Box
          title="View Project"
          ref={setNodeRef}
          {...attributes}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
        >
          <CardActionArea>
            <CardMedia
              sx={styles.media}
              image={thumbnail ? ProjectPreview : project.thumbnail}
            >
              <Box className="backdrop" sx={{ ...styles.backdrop, ...(showDeleteConfirmation && styles.standardBackdrop), ...(isInProgress && styles.successBackdrop) }} />
              <Box sx={styles.projectItemIcons} className="projectItemIcons">
                {hovered && (
                  <IconButton
                    id={`menu-icon-${project._id}`}
                    sx={styles.projectMenu}
                    size="small"
                    ref={menuItemRef}
                  >
                    <MoreHorizIcon sx={{ ...(styles.moreProjectMenuIcon), ...(isMenuOpen && styles.activeIcon) }} />
                  </IconButton>
                )}
                <Popper sx={styles.projectListItemPopper} open={isMenuOpen} anchorEl={anchorEl} transition>
                  {({ TransitionProps }) => (
                    <Fade {...TransitionProps} timeout={350}>
                      <Paper id={`menu-list-${project._id}`}>
                        <ClickAwayListener onClickAway={handleMenuClose}>
                          <MenuList sx={styles.menuList}>
                            <MenuItem
                              sx={styles.fontWeight}
                              id={`move-to-folder-${project._id}`}
                            >
                              Move To...
                            </MenuItem>
                            {!cannotDelete && !imageryRequested && !rasterRequested && (
                              <MenuItem
                                sx={styles.fontWeight}
                                id={`delete-project-${project._id}`}
                              >
                                Delete
                              </MenuItem>
                            )}
                          </MenuList>
                        </ClickAwayListener>
                      </Paper>
                    </Fade>
                  )}
                </Popper>
                {!opsTrainer && <Avatar sx={styles.avatar} title={project.creator}>{initials.join('').toLowerCase()}</Avatar>}
              </Box>
              {!showDeleteConfirmation && isInProgress && (
                <Box sx={styles.progressPanel}>
                  <Box sx={styles.progressStepperRoot}>
                    <Stepper sx={styles.stepper} activeStep={activeStep} alternativeLabel connector={<ColorlibConnector />}>
                      {steps.map((label, index) => (
                        <Step key={ActiveLabels[index]}>
                          <StepLabel StepIconComponent={ColorlibStepIcon} sx={styles.stepLabel}>
                            {label}
                          </StepLabel>
                        </Step>
                      ))}
                    </Stepper>
                  </Box>
                </Box>
              )}
              {!showDeleteConfirmation && !isInProgress && <Typography variant="h4" sx={styles.label} className="label">View Project</Typography>}
            </CardMedia>
          </CardActionArea>
          <CardActions sx={styles.cardActions}>
            <Box sx={{ ...(styles.bottomBackdrop), ...(isInProgress && styles.bottomSuccessBackdrop) }} />
            <Tooltip title={project.name || 'N/A'} arrow placement="top">
              <Box sx={styles.nameText}>
                <Typography variant="h5" sx={styles.titleText}>{ (opsTrainer ? project?.training?.nameEditable : project.name) || 'N/A'}</Typography>
              </Box>
            </Tooltip>
            <Typography sx={styles.dateText} variant="body2">
              <Box sx={styles.details}>
                {orderEstimatedDeliveryDate ? getDeliveryDateLabel() : project.updatedAt && moment(project.updatedAt).format('LL')}
                {orderEstimate && (
                  <Box sx={styles.detailsWrapper}>
                    <Brightness1Icon style={styles.notificationIcon} fontSize="inherit" htmlColor="#ff9900" />
                    <Typography sx={styles.dateText} variant="subtitle2">
                      Estimate
                    </Typography>
                  </Box>
                )}
                {(!orderEstimate && (orderStatus > orderStatusCode.autonomousFailed && orderStatus < orderStatusCode.canceled)) && (
                  <Box sx={styles.detailsWrapper}>
                    <Brightness1Icon sx={styles.notificationIcon} fontSize="inherit" htmlColor="#66ff66" />
                    <Typography sx={styles.dateText} variant="subtitle2">
                      Completed
                    </Typography>
                  </Box>
                )}
                {(!orderEstimate && orderStatus === orderStatusCode.canceled) && (
                  <Box sx={styles.detailsWrapper}>
                    <Brightness1Icon sx={styles.notificationIcon} fontSize="inherit" htmlColor="#ff0000" />
                    <Typography sx={styles.dateText} variant="subtitle2">
                      Canceled
                    </Typography>
                  </Box>
                )}
              </Box>
            </Typography>
          </CardActions>
        </Box>
      </Card>

      <DeleteProjectDialog
        showDialog={showDeleteConfirmation}
        setShowDialog={setShowDeleteConfirmation}
        projectId={project._id}
      />
      <MoveProjectToFolderDialog
        showDialog={showMoveProjectToFolderDialog}
        setShowDialog={() => setShowMoveProjectToFolderDialog(false)}
        projectId={project._id}
        projectFolderId={project.folderId}
        onSuccess={handleMoveProjectToFolderSuccess}
      />
    </Box>
  );
};

export default ProjectListItem;
